import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import FilterMultiSelectDropdown from "../../components/filterDropdown/FilterMultiSelectDropdown";
import FilterInput from "../../components/filterDropdown/FilterInput";
import {
  cleanFiltersForRequest,
  serializeMultiSelectFiltersForRequest,
} from "../../shared/filterUtils";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import {
  generateUniqueNumber,
  productionStatusOptions,
} from "../../shared/utility";

const reportTypeOptions = [{ value: 1, label: "Production Report" }];

function buildManufacturingAnalyticsStats(data) {
  const formatRatio = (part, total) => {
    const partCount = Number(part ?? 0);
    const totalCount = Number(total ?? 0);
    return `${partCount}/${totalCount}`;
  };

  const totalRecords = data?.totalRecords;

  return [
    {
      name: "In progress",
      stat: formatRatio(data?.inProgress?.total, totalRecords),
      icon: "inProgress",
    },
    {
      name: "Completed",
      stat: formatRatio(data?.completed?.total, totalRecords),
      icon: "completed",
    },
    {
      name: "Manufactured Weight",
      stat: data?.manufactured?.formatted ?? "—",
      icon: "manufacturedWeight",
    },
    {
      name: "Target Weight",
      stat: data?.totalManufacturedTargetWeight?.formatted ?? "—",
      icon: "targetWeight",
    },
  ];
}

const allStatusOption = { value: -1, label: "All statuses" };
const allProductsOption = { value: -1, label: "All Products" };
const statusFilterOptions = [allStatusOption, ...productionStatusOptions];

export function ManufacturedProductsViewModel(logout, addSnackbar) {
  const [analytics, setAnalytics] = useState({
    range: null,
    stats: [],
    loading: true,
  });
  const [showDates, setShowDates] = useState(false);
  const [filters, setFilters] = useState({
    product: [],
    status: [],
    supervisor: [],
    employee1: [],
    employee2: [],
    certification: [],
    recipe: [],
    grandTotal: null,
    startDate: null,
    endDate: null,
    lot_number: null,
  });
  const [reportFilters, setReportFilters] = useState({
    type: { value: -1, label: "Select report" },
  });

  const [certifications, setCertifications] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [productionHistories, setProductionHistories] = useState({ data: [] });
  const [recipes, setRecipes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [reportInProgress, setReportInProgress] = useState(false);
  const [productsForDropdown, setProductsForDropdown] = useState([]);

  const getManufacturedProducts = async function (search, page, filterArg) {
    setLoading(true);
    const payload = {
      page: page,
      filters: serializeMultiSelectFiltersForRequest(
        cleanFiltersForRequest({ ...filterArg, search: search }),
      ),
    };
    getAnalytics(search, filterArg);
    let callResult = await makePostApiCall("manufacturedProducts", payload);

    if (callResult.success) {
      setProductionHistories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);
  };

  const deleteManufacturedProduct = async function (id) {
    let callResult = await makePostApiCall(`deleteManufacturedProduct/${id}`);

    if (callResult.success) {
      const filtereditems = productionHistories.data.filter(
        (item) => item.id !== id,
      );
      setProductionHistories({ ...productionHistories, data: filtereditems });

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeleteManufacturedProduct = function (product) {
    let product_name = product.product_name ? product.product_name : "product";
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteManufacturedProduct(product.id)}
          message={`Are you sure you want to ${product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const resetFilters = () => {
    const filterReset = {
      product: [],
      status: [],
      supervisor: [],
      employee1: [],
      employee2: [],
      certification: [],
      recipe: [],
      grandTotal: null,
      startDate: null,
      endDate: null,
      lot_number: null,
    };
    setFilters(filterReset);

    setShowFilters(false);
    getManufacturedProducts(null, 1, filterReset);
  };

  const getUsersForDropdown = async function (type, department) {
    let callResult = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      if (type === 1) {
        setEmployeeOptions(callResult.data);
      } else {
        setSupervisorOptions(callResult.data);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchProductsForDropdown = async function () {
    let callResult = await makePostApiCall(`fetchProductsForDropdown`);

    if (callResult.success) {
      setProductsForDropdown([allProductsOption, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCertifiedByOptionsForDropdown = async function () {
    let callResult = await makePostApiCall(`certifiedByOptionsForDropdown`);

    if (callResult.success) {
      setCertifications([
        { value: -1, label: "All Certifications" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchRecipesForDropdown = async function () {
    let callResult = await makePostApiCall(`recipesForDropdown`);

    if (callResult.success) {
      setRecipes([{ value: -1, label: "All Recipes" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const filterComponents = [
    <FilterMultiSelectDropdown
      key="product"
      header="Product"
      onChange={(value) =>
        setFilters({
          ...filters,
          product: value,
        })
      }
      options={
        productsForDropdown.length ? productsForDropdown : [allProductsOption]
      }
      resetFilter={() =>
        setFilters({
          ...filters,
          product: [],
        })
      }
      valueName="product"
      value={filters.product}
    />,
    <FilterMultiSelectDropdown
      key="status"
      header="Status"
      onChange={(value) =>
        setFilters({
          ...filters,
          status: value,
        })
      }
      options={statusFilterOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          status: [],
        })
      }
      valueName="status"
      value={filters.status}
    />,
    <FilterInput
      key="lot_number"
      header="Search by Lot Number"
      onChange={(value) =>
        setFilters({
          ...filters,
          lot_number: value,
        })
      }
      resetFilter={() =>
        setFilters({
          ...filters,
          lot_number: null,
        })
      }
      valueName="lot_number"
      value={filters.lot_number}
    />,
    <FilterMultiSelectDropdown
      key="certification"
      header="Certification"
      onChange={(value) =>
        setFilters({
          ...filters,
          certification: value,
        })
      }
      options={certifications}
      resetFilter={() =>
        setFilters({
          ...filters,
          certification: [],
        })
      }
      valueName="certification"
      value={filters.certification}
    />,
    <FilterMultiSelectDropdown
      key="recipe"
      header="Recipe"
      onChange={(value) =>
        setFilters({
          ...filters,
          recipe: value,
        })
      }
      options={recipes}
      resetFilter={() =>
        setFilters({
          ...filters,
          recipe: [],
        })
      }
      valueName="recipe"
      value={filters.recipe}
    />,
    <FilterMultiSelectDropdown
      key="supervisor"
      header="Supervisor"
      onChange={(value) =>
        setFilters({
          ...filters,
          supervisor: value,
        })
      }
      options={supervisorOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          supervisor: [],
        })
      }
      valueName="supervisor"
      value={filters.supervisor}
    />,
    <FilterMultiSelectDropdown
      key="employee1"
      header="Employee 1"
      onChange={(value) =>
        setFilters({
          ...filters,
          employee1: value,
        })
      }
      options={employeeOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          employee1: [],
        })
      }
      valueName="employee1"
      value={filters.employee1}
    />,
  ];

  const generateProductionReport = async function () {
    setReportInProgress(true);
    let callResult = await makePostApiCall(
      `generateProductionHistoriesReport`,
      {
        ...reportFilters,
        endDate: filters.endDate,
        startDate: filters.startDate,
      },
    );
    if (callResult.success) {
      setShowReports(false);
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setReportInProgress(false);
  };

  const reportComponents = [
    <FilterSingleSelectDropdown
      header="Report type"
      onChange={(value) =>
        setReportFilters({
          ...reportFilters,
          type: value,
        })
      }
      options={reportTypeOptions}
      resetFilter={() =>
        setReportFilters({
          ...reportFilters,
          type: { value: -1, label: "Select report" },
        })
      }
      valueName="type"
      value={reportFilters.type}
    />,
  ];

  const getAnalytics = async function (search, filterArg) {
    setAnalytics((prev) => ({ ...prev, loading: true }));
    const payload = {
      filters: serializeMultiSelectFiltersForRequest(
        cleanFiltersForRequest({ ...filterArg, search: search }),
      ),
    };
    let callResult = await makePostApiCall(
      `fetchManufacturingAnalytics`,
      payload,
    );
    if (callResult.success) {
      const data = callResult.data;
      setAnalytics({
        loading: false,
        range: data.range,
        totalRecords: data.totalRecords,
        stats: buildManufacturingAnalyticsStats(data),
      });
    } else {
      setAnalytics((prev) => ({ ...prev, loading: false }));
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not load analytics");
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  return {
    emptyModal,
    fetchRecipesForDropdown,
    getCertifiedByOptionsForDropdown,
    getManufacturedProducts,
    loading,
    productionHistories,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    askDeleteManufacturedProduct,
    filters,
    resetFilters,
    showFilters,
    setShowFilters,
    filterComponents,
    getUsersForDropdown,
    generateProductionReport,
    reportComponents,
    showReports,
    setShowReports,
    reportInProgress,
    analytics,
    setFilters,
    setShowDates,
    showDates,
    fetchProductsForDropdown,
    productsForDropdown,
    certifications,
    recipes,
    supervisorOptions,
    employeeOptions,
    statusFilterOptions,
  };
}
